import { useShadowRootElements } from '@backstage/plugin-techdocs-react';
import { Box, Button, IconButton, Tooltip, Typography, makeStyles, useTheme } from '@material-ui/core';
import React, { useCallback, useEffect, useState } from 'react';

/** Same storage key as the contrib TextSize addon so existing preferences carry over. */
export const TEXT_SIZE_STORAGE_KEY = 'techdocs.addons.settings.textsize';
export const TEXT_SIZES = [90, 100, 115, 130, 150];
export const DEFAULT_TEXT_SIZE = 100;

const useStyles = makeStyles(theme => ({
  root: {
    display: 'inline-flex',
    alignItems: 'center',
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    height: 30,
  },
  step: {
    width: 30,
    height: 28,
    borderRadius: 0,
    fontWeight: theme.typography.fontWeightBold as number,
  },
  small: {
    fontSize: '0.8rem',
  },
  large: {
    fontSize: '1.1rem',
  },
  value: {
    minWidth: 52,
    height: 28,
    padding: theme.spacing(0, 0.5),
    borderRadius: 0,
    borderLeft: `1px solid ${theme.palette.divider}`,
    borderRight: `1px solid ${theme.palette.divider}`,
    textTransform: 'none',
  },
}));

const readStoredSize = (): number => {
  try {
    const stored = Number.parseInt(window.localStorage.getItem(TEXT_SIZE_STORAGE_KEY) ?? '', 10);
    return TEXT_SIZES.includes(stored) ? stored : DEFAULT_TEXT_SIZE;
  } catch {
    return DEFAULT_TEXT_SIZE;
  }
};

/**
 * Always-visible text size control for the TechDocs subheader, replacing the
 * contrib slider that was hidden behind the settings (cog) menu.
 */
export const TextSizeControl = () => {
  const classes = useStyles();
  const theme = useTheme();
  const [body] = useShadowRootElements<HTMLBodyElement>(['body']);
  const [size, setSize] = useState<number>(readStoredSize);

  useEffect(() => {
    if (!body) {
      return;
    }
    const htmlFontSize = (theme.typography as { htmlFontSize?: number }).htmlFontSize ?? 16;
    body.style.setProperty('--md-typeset-font-size', `${htmlFontSize * (size / 100)}px`);
  }, [body, size, theme]);

  const update = useCallback((next: number) => {
    setSize(next);
    try {
      window.localStorage.setItem(TEXT_SIZE_STORAGE_KEY, String(next));
    } catch {
      // Storage may be unavailable (private mode); the size still applies.
    }
  }, []);

  const index = TEXT_SIZES.indexOf(size);

  return (
    <Box className={classes.root} role="group" aria-label="Text size">
      <Tooltip title="Smaller text">
        <span>
          <IconButton
            size="small"
            className={`${classes.step} ${classes.small}`}
            aria-label="Decrease text size"
            disabled={index <= 0}
            onClick={() => update(TEXT_SIZES[index - 1])}
          >
            A
          </IconButton>
        </span>
      </Tooltip>
      <Tooltip title="Reset text size">
        <Button
          size="small"
          className={classes.value}
          aria-label={`Text size ${size}%, reset to ${DEFAULT_TEXT_SIZE}%`}
          onClick={() => update(DEFAULT_TEXT_SIZE)}
        >
          <Typography variant="caption">{size}%</Typography>
        </Button>
      </Tooltip>
      <Tooltip title="Larger text">
        <span>
          <IconButton
            size="small"
            className={`${classes.step} ${classes.large}`}
            aria-label="Increase text size"
            disabled={index >= TEXT_SIZES.length - 1}
            onClick={() => update(TEXT_SIZES[index + 1])}
          >
            A
          </IconButton>
        </span>
      </Tooltip>
    </Box>
  );
};
